import { useTranslation } from 'react-i18next';
import { StatusBadge, type StatusBadgeStatus } from '@xxtract/ui';
import type { IntakeListStatus } from '../intake.api';

const STATUS_TO_BADGE: Record<IntakeListStatus, StatusBadgeStatus> = {
  uploaded: 'process-future',
  interpreting: 'process-current',
  review: 'process-on-hold',
  submitting: 'process-current',
  submitted: 'process-complete',
  expired: 'process-no-data',
  received: 'process-complete',
  // spec-cp-aanmeldingen-in-lijst CAP-4: CustomerPortal telt nog niet afgeronde producten.
  notCompleted: 'process-on-hold',
};

/**
 * h6-3 (EXP IA/lijst-merge): zodra er een ordernummer is, is de aanmelding geen aanmelding meer maar
 * een order. Vanaf dat moment leest de regel als **Ontvangen** met dat nummer erbij — de lokale
 * minimumstatus — en niet als "ingediend".
 *
 * Waarom die minimumstatus bestaat: het eerste echte procesbericht kan seconden tot minuten later
 * binnenkomen. Zonder deze regel leest de klant in dat gat "geen procesdata", vlak nadat hij een
 * succesmelding heeft gezien. Zodra de echte processtap er is, wint die (h6-4).
 */
export function IntakeStatusBadge({ status, orderNumber }: { status: IntakeListStatus; orderNumber?: string | null }) {
  const { t } = useTranslation();
  const getoond = status === 'submitted' && orderNumber ? 'received' : status;

  if (orderNumber && (getoond === 'received' || getoond === 'notCompleted')) {
    return (
      <span className="inline-flex items-center gap-2">
        <StatusBadge label={t(`intake.list.status.${getoond}`)} status={STATUS_TO_BADGE[getoond]} />
        <span data-testid="intake-order-number" className="text-[var(--foreground-muted)]">
          {t('intake.list.orderNumber', { orderNumber })}
        </span>
      </span>
    );
  }

  return <StatusBadge label={t(`intake.list.status.${getoond}`)} status={STATUS_TO_BADGE[getoond]} />;
}
